import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { lawyerService } from '../services/firebaseService';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { 
  ArrowLeft,
  User,
  Camera,
  Edit,
  Save,
  X,
  Plus,
  FileText,
  Award,
  GraduationCap,
  Briefcase,
  MapPin,
  Phone,
  Mail,
  Globe,
  Calendar,
  Check,
  Building,
  BookOpen,
  Clock,
  Building2,
  GraduationCap as GraduationCapIcon,
  Award as AwardIcon,
  Linkedin,
  Facebook,
  Instagram,
  Twitter,
  Upload,
  CreditCard,
  Shield,
  Trash2,
  DollarSign,
  MessageSquare,
  Video,
  Zap
} from 'lucide-react';

const LawyerProfileSettings = () => {
  const navigate = useNavigate();
  const { lawyers, setLawyers, user } = useApp();
  const { user: currentUser } = useFirebaseAuth();
  const [activeTab, setActiveTab] = useState('profile');
  
  // Handle navigation state for direct tab access
  useEffect(() => {
    const location = window.location;
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, []);

  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const fileInputRef = useRef(null);
  const [idProofType, setIdProofType] = useState('');
  const [idProofFile, setIdProofFile] = useState(null);
  const [barCouncilFile, setBarCouncilFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  
  // Load lawyer profile data from Firestore
  const loadLawyerProfileFromFirestore = async () => {
    if (!currentUser?.uid) {
      console.log('❌ No currentUser.uid found');
      return null;
    }
    
    try {
      console.log('🔄 Loading lawyer profile from Firestore for UID:', currentUser.uid);
      console.log('🔄 Current user object:', currentUser);
      
      // First, let's try to query the lawyers collection directly to debug
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      console.log('🔍 Directly querying lawyers collection...');
      const lawyersQuery = query(collection(db, 'lawyers'), where('uid', '==', currentUser.uid));
      const querySnapshot = await getDocs(lawyersQuery);
      
      console.log('📊 Direct query result - empty:', querySnapshot.empty);
      console.log('📊 Direct query result - docs count:', querySnapshot.docs.length);
      
      if (!querySnapshot.empty) {
        const lawyerDoc = querySnapshot.docs[0];
        console.log('✅ Found lawyer document directly:', lawyerDoc.id);
        console.log('✅ Lawyer document data:', lawyerDoc.data());
        return { id: lawyerDoc.id, ...lawyerDoc.data() };
      }
      
      // If direct query didn't work, try the service
      console.log('🔍 Trying lawyerService.getLawyerByUserId...');
      const result = await lawyerService.getLawyerByUserId(currentUser.uid);
      console.log('📊 Raw result from lawyerService:', result);
      
      if (result.success && result.data) {
        console.log('✅ Lawyer profile loaded from Firestore via service:', result.data);
        return result.data;
      } else {
        console.log('❌ No lawyer profile found in Firestore');
        console.log('❌ Result success:', result.success);
        console.log('❌ Result data:', result.data);
        return null;
      }
    } catch (error) {
      console.error('❌ Error loading lawyer profile from Firestore:', error);
      return null;
    }
  };

  // Get current lawyer profile from context or localStorage (fallback)
  const getCurrentLawyerProfile = () => {
    const userId = user?.id || user?.uid || currentUser?.uid;
    console.log('Getting lawyer profile for user:', userId);
    console.log('User object from context:', user);
    console.log('Current user from Firebase:', currentUser);
    console.log('Lawyers in context:', lawyers);
    
    // First try to get from context
    if (lawyers && lawyers.length > 0) {
      const currentLawyer = lawyers.find(lawyer => lawyer.userId === userId);
      if (currentLawyer) {
        console.log('Found lawyer in context:', currentLawyer);
        return currentLawyer;
      }
    }
    
    // Fallback to localStorage
    try {
      const storedLawyers = JSON.parse(localStorage.getItem('lawyers') || '[]');
      console.log('Stored lawyers in localStorage:', storedLawyers);
      const currentLawyer = storedLawyers.find(lawyer => lawyer.userId === userId);
      if (currentLawyer) {
        console.log('Found lawyer in localStorage:', currentLawyer);
        return currentLawyer;
      }
      console.log('No lawyer found for user ID:', userId);
      return null;
    } catch (error) {
      console.error('Error reading lawyer data:', error);
      return null;
    }
  };

  // Profile Data - Initialize with empty values first
  const [profileData, setProfileData] = useState({
    // Basic Information (exact match to registration)
    profilePicture: null,
    fullName: '',
    mobileNumber: '',
    location: '',
    professionalBio: '',
    barCouncilLicense: '',
    specialization: [],
    yearsOfPractice: '',
    education: [{ qualification: '', degreeCollege: '' }],
    yearOfPassing: '',
    chamberAddress: '',
    languages: [],
    achievements: [],
    
    // Contact Information
    gmail: '',
    linkedin: '',
    facebook: '',
    instagram: '',
    twitter: '',
    
    // Consultation Fees (exact match to registration)
    textConsultationFee: '',
    callConsultationFee: '',
    videoCallConsultationFee: '',
    inPersonConsultationFee: '',
    emergencyConsultationAvailable: false,
    emergencyConsultationFee: '',
    
    // Availability
    availableDays: [],
    timeSlots: {},
    
    // Documents
    idProofType: '',
    idProof: null,
    barCouncilCertificate: null,
    selfieWithId: null,
    
    // File Reading Charges
    fileReadingCharges: [],
    
    // Bank Details
    accountNumber: '',
    accountHolderName: '',
    bankName: '',
    ifscCode: ''
  });

  // Load lawyer profile data when user becomes available
  useEffect(() => {
    const loadProfileData = async () => {
      // First try to load from Firestore
      const firestoreLawyer = await loadLawyerProfileFromFirestore();
      
      if (firestoreLawyer) {
        console.log('✅ Loading profile data from Firestore');
        console.log('📋 Raw Firestore data:', firestoreLawyer);
        
                 setProfileData({
           // Basic Information (exact mapping from LawyerRegistration)
           profilePicture: firestoreLawyer.profile_pic || null,
           fullName: firestoreLawyer.full_name || '',
           mobileNumber: firestoreLawyer.phone || '',
           location: firestoreLawyer.bar_council_state || '',
           professionalBio: firestoreLawyer.bio || '',
           barCouncilLicense: firestoreLawyer.bar_council_number || '',
           specialization: Array.isArray(firestoreLawyer.specialization) ? firestoreLawyer.specialization : [],
           yearsOfPractice: firestoreLawyer.experience_years?.toString() || '',
           
           // Education (mapped from shortened keys)
           education: firestoreLawyer.education?.map(edu => ({
             qualification: edu.q || edu.qualification || '',
             degreeCollege: edu.c || edu.degreeCollege || ''
           })) || [{ qualification: '', degreeCollege: '' }],
           
                       yearOfPassing: firestoreLawyer.year_of_passing || firestoreLawyer.education?.[0]?.y || '',
           chamberAddress: firestoreLawyer.chamber_address || '',
                       languages: Array.isArray(firestoreLawyer.languages) ? firestoreLawyer.languages : (firestoreLawyer.languages ? [firestoreLawyer.languages] : []),
           achievements: Array.isArray(firestoreLawyer.achievements) ? firestoreLawyer.achievements : (firestoreLawyer.achievements ? [firestoreLawyer.achievements] : []),
           
           // Contact Information
           gmail: firestoreLawyer.email || '',
           linkedin: firestoreLawyer.social?.linkedin || '',
           facebook: firestoreLawyer.social?.facebook || '',
           instagram: firestoreLawyer.social?.instagram || '',
           twitter: firestoreLawyer.social?.twitter || '',
           
           // Consultation Fees (mapped from Firestore structure)
           textConsultationFee: firestoreLawyer.fees?.text?.toString() || '',
           callConsultationFee: firestoreLawyer.fees?.call?.toString() || '',
           videoCallConsultationFee: firestoreLawyer.fees?.video?.toString() || '',
           inPersonConsultationFee: firestoreLawyer.fees?.in_person?.toString() || '',
           emergencyConsultationAvailable: firestoreLawyer.emergency_available || false,
           emergencyConsultationFee: firestoreLawyer.fees?.emergency?.toString() || '',
           
           // Availability
           availableDays: Array.isArray(firestoreLawyer.available_days) ? firestoreLawyer.available_days : [],
           timeSlots: firestoreLawyer.time_slots || {},
           
           // Documents
           idProofType: firestoreLawyer.id_proof_type || '',
           idProof: firestoreLawyer.id_proof || null,
           barCouncilCertificate: firestoreLawyer.bar_council_certificate || null,
           selfieWithId: firestoreLawyer.selfie_with_id || null,
           
                       // File Reading Charges
           fileReadingCharges: Array.isArray(firestoreLawyer.file_charges) ? firestoreLawyer.file_charges : [],
           
           // Bank Details (mapped from Firestore structure)
           accountNumber: firestoreLawyer.bank?.acc || '',
           accountHolderName: firestoreLawyer.bank?.name || '',
           bankName: firestoreLawyer.bank?.bank || '',
           ifscCode: firestoreLawyer.bank?.ifsc || ''
         });
        
         console.log('✅ Profile data loaded from Firestore');
       } else {
         console.log('❌ No Firestore data found, loading from context/localStorage...');
         const currentLawyer = getCurrentLawyerProfile();
         console.log('Loading lawyer profile from context/localStorage:', currentLawyer);
         if (currentLawyer) {
          setProfileData({
            // Basic Information (exact match to registration)
            profilePicture: currentLawyer.profilePicture || null,
            fullName: currentLawyer.fullName || '',
            mobileNumber: currentLawyer.mobileNumber || '',
            location: currentLawyer.location || '',
            professionalBio: currentLawyer.professionalBio || '',
            barCouncilLicense: currentLawyer.barCouncilLicense || '',
            specialization: Array.isArray(currentLawyer.specialization) ? currentLawyer.specialization : (currentLawyer.specialization ? [currentLawyer.specialization] : []),
            yearsOfPractice: currentLawyer.yearsOfPractice || '',
            education: currentLawyer.education || [{ qualification: '', degreeCollege: '' }],
            yearOfPassing: currentLawyer.yearOfPassing || '',
            chamberAddress: currentLawyer.chamberAddress || '',
            languages: currentLawyer.languages || [],
            achievements: currentLawyer.achievements || [],
            
            // Contact Information
            gmail: currentLawyer.gmail || '',
            linkedin: currentLawyer.linkedin || '',
            facebook: currentLawyer.facebook || '',
            instagram: currentLawyer.instagram || '',
            twitter: currentLawyer.twitter || '',
            
            // Consultation Fees (exact match to registration)
            textConsultationFee: currentLawyer.textConsultationFee || '',
            callConsultationFee: currentLawyer.callConsultationFee || '',
            videoCallConsultationFee: currentLawyer.videoCallConsultationFee || '',
            inPersonConsultationFee: currentLawyer.inPersonConsultationFee || '',
            emergencyConsultationAvailable: currentLawyer.emergencyConsultationAvailable || false,
            emergencyConsultationFee: currentLawyer.emergencyConsultationFee || '',
            
            // Availability
            availableDays: currentLawyer.availableDays || [],
            timeSlots: currentLawyer.timeSlots || {},
            
            // Documents
            idProofType: currentLawyer.idProofType || '',
            idProof: currentLawyer.idProof || null,
            barCouncilCertificate: currentLawyer.barCouncilCertificate || null,
            selfieWithId: currentLawyer.selfieWithId || null,
            
            // File Reading Charges
            fileReadingCharges: currentLawyer.fileReadingCharges || [],
            
            // Bank Details
            accountNumber: currentLawyer.accountNumber || '',
            accountHolderName: currentLawyer.accountHolderName || '',
            bankName: currentLawyer.bankName || '',
            ifscCode: currentLawyer.ifscCode || ''
          });
        }
      }
    };

    if (currentUser?.uid) {
    loadProfileData();
    }
  }, [currentUser]);

  const [tempData, setTempData] = useState({});
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);

  // Data arrays from registration form
  const legalSpecializations = [
    'Criminal Law', 'Civil Law', 'Corporate Law', 'Family Law', 'Property Law',
    'Constitutional Law', 'Tax Law', 'Intellectual Property Law', 'Environmental Law',
    'Labor Law', 'Banking Law', 'Insurance Law', 'Real Estate Law', 'Immigration Law',
    'International Law', 'Media Law', 'Sports Law', 'Healthcare Law', 'Technology Law'
  ];

  const availableLanguages = [
    'English', 'Hindi', 'Marathi', 'Gujarati', 'Bengali', 'Tamil', 'Telugu',
    'Kannada', 'Malayalam', 'Punjabi', 'Urdu', 'Odia', 'Assamese', 'Sanskrit'
  ];

  const indianCities = [
    'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 'Pune',
    'Ahmedabad', 'Jaipur', 'Surat', 'Lucknow', 'Kanpur', 'Nagpur', 'Indore',
    'Thane', 'Bhopal', 'Visakhapatnam', 'Pimpri-Chinchwad', 'Patna', 'Vadodara'
  ];

  useEffect(() => {
    if (profileData && Object.keys(profileData).length > 0) {
      setTempData(profileData);
      setSelectedSpecializations(profileData.specialization || []);
      setSelectedLanguages(profileData.languages || []);
    }
  }, [profileData]);

    const handleSave = async () => {
    if (!currentUser?.uid) {
      console.error('No current user UID found');
      return;
    }

    setIsSaving(true);
    
    try {
      console.log('🔄 Saving lawyer profile to Firestore...');
      
      // Convert profile data to Firestore format (matching LawyerRegistration structure)
      const firestoreData = {
        // Basic Information (exact mapping to Firestore fields)
        full_name: tempData.fullName,
        phone: tempData.mobileNumber,
        bar_council_state: tempData.location,
        bio: tempData.professionalBio,
        bar_council_number: tempData.barCouncilLicense,
        specialization: tempData.specialization,
        experience_years: tempData.yearsOfPractice,
        
        // Education (convert to shortened format)
        education: tempData.education?.map(edu => ({
          q: edu.qualification,
          c: edu.degreeCollege,
          y: tempData.yearOfPassing
        })) || [],
        year_of_passing: tempData.yearOfPassing,
        chamber_address: tempData.chamberAddress,
        languages: tempData.languages,
        achievements: tempData.achievements,
        
        // Contact Information
        email: tempData.gmail,
        social: {
          linkedin: tempData.linkedin,
          facebook: tempData.facebook,
          instagram: tempData.instagram,
          twitter: tempData.twitter
        },
        
        // Consultation Fees (exact mapping to Firestore structure)
        fees: {
          text: parseInt(tempData.textConsultationFee) || 0,
          call: parseInt(tempData.callConsultationFee) || 0,
          video: parseInt(tempData.videoCallConsultationFee) || 0,
          in_person: parseInt(tempData.inPersonConsultationFee) || 0,
          emergency: parseInt(tempData.emergencyConsultationFee) || 0
        },
        emergency_available: tempData.emergencyConsultationAvailable,
        
        // Availability
        available_days: tempData.availableDays,
        time_slots: tempData.timeSlots,
        
        // Documents
        id_proof_type: tempData.idProofType,
        id_proof: tempData.idProof,
        bar_council_certificate: tempData.barCouncilCertificate,
        selfie_with_id: tempData.selfieWithId,
        
        // File Reading Charges
        file_charges: tempData.fileReadingCharges,
        
        // Bank Details (exact mapping to Firestore structure)
        bank: {
          acc: tempData.accountNumber,
          name: tempData.accountHolderName,
          bank: tempData.bankName,
          ifsc: tempData.ifscCode
        },
        
        // Profile Picture
        profile_pic: tempData.profilePicture,
        
        // Update timestamp
        updated_at: new Date().toISOString()
      };

      console.log('📋 Data to save to Firestore:', firestoreData);

      // Save to Firestore using the lawyerService
      const result = await lawyerService.updateLawyer(currentUser.uid, firestoreData);
      
      if (result.success) {
        console.log('✅ Profile saved successfully to Firestore');
        
        // Update local state
        setProfileData(tempData);
        setIsEditing(false);
        setShowSuccess(true);
        
        // Hide success message after 3 seconds
        setTimeout(() => setShowSuccess(false), 3000);
        
        // Also update localStorage and context for backward compatibility
        const updatedLawyer = {
          ...getCurrentLawyerProfile(),
          ...tempData
        };
        
        const userId = user?.id || user?.uid || currentUser?.uid;
        const storedLawyers = JSON.parse(localStorage.getItem('lawyers') || '[]');
        const updatedLawyers = storedLawyers.map(lawyer => 
          lawyer.userId === userId ? updatedLawyer : lawyer
        );
        localStorage.setItem('lawyers', JSON.stringify(updatedLawyers));
        setLawyers(updatedLawyers);
        
      } else {
        console.error('❌ Failed to save profile to Firestore:', result.error);
        alert('Failed to save profile. Please try again.');
      }
      
    } catch (error) {
      console.error('❌ Error saving profile to Firestore:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTempData(profileData);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setTempData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper functions for managing selections
  const toggleSpecialization = (spec) => {
    const newSpecs = selectedSpecializations.includes(spec)
      ? selectedSpecializations.filter(s => s !== spec)
      : [...selectedSpecializations, spec];
    setSelectedSpecializations(newSpecs);
    handleInputChange('specialization', newSpecs);
  };

  const toggleLanguage = (lang) => {
    const newLangs = selectedLanguages.includes(lang)
      ? selectedLanguages.filter(l => l !== lang)
      : [...selectedLanguages, lang];
    setSelectedLanguages(newLangs);
    handleInputChange('languages', newLangs);
  };

  // Profile Picture Functions
  const handleProfilePictureClick = () => {
    if (isEditing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleProfilePictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
        handleInputChange('profilePicture', e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // File upload functions
  const handleFileUpload = (fileType, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        switch (fileType) {
          case 'idProof':
            setIdProofFile(e.target.result);
            handleInputChange('idProof', e.target.result);
            break;
          case 'barCouncil':
            setBarCouncilFile(e.target.result);
            handleInputChange('barCouncilCertificate', e.target.result);
            break;
          case 'selfie':
            setSelfieFile(e.target.result);
            handleInputChange('selfieWithId', e.target.result);
            break;
          default:
            break;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = (fileType) => {
    switch (fileType) {
      case 'idProof':
        setIdProofFile(null);
        handleInputChange('idProof', null);
        break;
      case 'barCouncil':
        setBarCouncilFile(null);
        handleInputChange('barCouncilCertificate', null);
        break;
      case 'selfie':
        setSelfieFile(null);
        handleInputChange('selfieWithId', null);
        break;
      default:
        break;
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'professional', label: 'Professional Details', icon: Briefcase },
    { id: 'consultation', label: 'Consultation Rates', icon: DollarSign },
    { id: 'accounts', label: 'Accounts & Documents', icon: FileText }
  ];

  // Check if we have profile data loaded from Firestore or context
  const hasProfileData = profileData && (
    profileData.fullName || 
    profileData.mobileNumber || 
    profileData.location || 
    profileData.professionalBio || 
    profileData.barCouncilLicense ||
    (profileData.specialization && profileData.specialization.length > 0) ||
    profileData.yearsOfPractice ||
    (profileData.education && profileData.education.length > 0 && profileData.education[0].qualification) ||
    profileData.yearOfPassing ||
    profileData.chamberAddress ||
    (profileData.languages && profileData.languages.length > 0) ||
    (profileData.achievements && profileData.achievements.length > 0) ||
    profileData.gmail ||
    profileData.linkedin ||
    profileData.facebook ||
    profileData.instagram ||
    profileData.twitter ||
    profileData.textConsultationFee ||
    profileData.callConsultationFee ||
    profileData.videoCallConsultationFee ||
    profileData.inPersonConsultationFee ||
    profileData.emergencyConsultationFee ||
    (profileData.availableDays && profileData.availableDays.length > 0) ||
    profileData.idProofType ||
    profileData.idProof ||
    profileData.barCouncilCertificate ||
    profileData.selfieWithId ||
    (profileData.fileReadingCharges && profileData.fileReadingCharges.length > 0) ||
    profileData.accountNumber ||
    profileData.accountHolderName ||
    profileData.bankName ||
    profileData.ifscCode
  );
  
  // Debug logging
  console.log('🔍 Debug: profileData state:', profileData);
  console.log('🔍 Debug: hasProfileData result:', hasProfileData);
  console.log('🔍 Debug: profileData keys:', profileData ? Object.keys(profileData) : 'No profileData');
  
  // If no profile data exists, show message with debug options
  if (!hasProfileData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-lavender-50">
        <div className="max-w-md mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/lawyer-settings')}
              className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200"
            >
              <ArrowLeft className="w-5 h-5 text-purple-500" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Profile Settings</h1>
              <p className="text-sm text-gray-600">Manage your professional profile</p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
            <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Profile Found</h3>
            <p className="text-gray-600 mb-6">You need to create a lawyer profile first.</p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/lawyer-profile')}
                className="w-full px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
              >
                Create Profile
              </button>
              
              <button
                onClick={async () => {
                  console.log('🔄 Debug: Loading profile from Firestore...');
                  console.log('🔄 Current user:', currentUser);
                  console.log('🔄 Current user UID:', currentUser?.uid);
                  const firestoreLawyer = await loadLawyerProfileFromFirestore();
                  console.log('📋 Debug: Firestore result:', firestoreLawyer);
                  if (firestoreLawyer) {
                    console.log('✅ Debug: Profile found in Firestore!');
                    // Force reload the component
                    window.location.reload();
                  } else {
                    console.log('❌ Debug: No profile found in Firestore');
                  }
                }}
                className="w-full px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Debug: Check Firestore
              </button>
              
                             <div className="text-xs text-gray-500 bg-gray-100 p-3 rounded-lg">
                 <div><strong>Debug Info:</strong></div>
                 <div>Firebase UID: {currentUser?.uid || 'Not found'}</div>
                 <div>User Email: {currentUser?.email || 'Not found'}</div>
                 <div>Context User ID: {user?.id || user?.uid || 'Not found'}</div>
                 <div>Resolved User ID: {user?.id || user?.uid || currentUser?.uid || 'Not found'}</div>
               </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-lavender-50">
      <div className="max-w-md mx-auto px-4 py-6">
                           {/* Success Message */}
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="fixed top-4 right-4 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 text-sm"
            >
              <Check className="w-4 h-4" />
              Profile saved to database successfully!
            </motion.div>
          )}

                   

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/lawyer-settings')}
                className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200"
              >
                <ArrowLeft className="w-5 h-5 text-purple-500" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Profile Settings</h1>
                <p className="text-sm text-gray-600">Manage your professional profile</p>
              </div>
            </div>
                         <div className="flex items-center gap-2">
               {isEditing ? (
                 <>
                                       <button
                      onClick={handleSave}
                      disabled={isSaving}
                      className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center gap-1 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSaving ? (
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save className="w-3 h-3" />
                      )}
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                   <button
                     onClick={handleCancel}
                     className="px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg flex items-center gap-1 transition-colors text-sm"
                   >
                     <X className="w-3 h-3" />
                     Cancel
                   </button>
                 </>
               ) : (
                 <button
                   onClick={() => setIsEditing(true)}
                   className="px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg flex items-center gap-1 transition-colors text-sm"
                 >
                   <Edit className="w-3 h-3" />
                   Edit
                 </button>
               )}
             </div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-6"
        >
                                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1">
              <div className="grid grid-cols-4 gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex flex-col items-center justify-center gap-1 py-3 px-2 rounded-lg transition-all duration-200 text-xs ${
                    activeTab === tab.id
                      ? 'bg-purple-500 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200"
        >
          {/* Profile Tab - Exact match to registration form */}
          {activeTab === 'profile' && (
            <div className="p-4">
              <div className="space-y-4">
                                 {/* Profile Picture */}
                 <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                   <div className="flex items-center gap-4">
                     <div className="relative">
                       {profileImage || tempData.profilePicture ? (
                         <img
                           src={profileImage || tempData.profilePicture}
                           alt="Profile"
                           className="w-20 h-20 rounded-full object-cover border-2 border-purple-200"
                         />
                       ) : (
                         <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                           {tempData.fullName?.charAt(0) || 'S'}
                         </div>
                       )}
                       {isEditing && (
                         <button 
                           onClick={handleProfilePictureClick}
                           className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white hover:bg-purple-600 transition-colors"
                         >
                           <Camera className="w-3 h-3" />
                         </button>
                       )}
                       <input
                         ref={fileInputRef}
                         type="file"
                         accept="image/*"
                         onChange={handleProfilePictureChange}
                         className="hidden"
                       />
                     </div>
                     <div>
                       <h3 className="text-lg font-semibold text-gray-900">Profile Picture</h3>
                       <p className="text-sm text-gray-600">
                         {isEditing ? 'Click the camera icon to upload a photo' : 'Upload a professional photo'}
                       </p>
                     </div>
                   </div>
                 </div>

                {/* Full Name */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                  <div className="flex items-center">
                    <User className="w-5 h-5 text-purple-600 mr-3" />
                    <input
                      type="text"
                      value={tempData.fullName || ''}
                      onChange={(e) => handleInputChange('fullName', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Full Name"
                      className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                    />
                  </div>
                </div>

                {/* Mobile Number */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 text-purple-600 mr-3" />
                    <input
                      type="tel"
                      value={tempData.mobileNumber || ''}
                      onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Mobile Number (Private, admin-only)"
                      className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">This number is stored but not shown to users.</p>
                </div>

                                 {/* Location */}
                 <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                   <div className="flex items-start">
                     <MapPin className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                     <div className="flex-1">
                       <div className="text-xs text-gray-800 mb-2">Location</div>
                       <div className="flex flex-wrap gap-2 mb-3">
                         {indianCities.map((city) => (
                           <button
                             key={city}
                             onClick={() => handleInputChange('location', city)}
                             disabled={!isEditing}
                             className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                               tempData.location === city
                                 ? 'bg-purple-500 text-white'
                                 : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                             } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                           >
                             {city}
                           </button>
                         ))}
                       </div>
                       {isEditing && (
                         <div className="flex items-center gap-2">
                           <input
                             type="text"
                             placeholder="Add custom city..."
                             className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                             onKeyPress={(e) => {
                               if (e.key === 'Enter' && e.target.value.trim()) {
                                 handleInputChange('location', e.target.value.trim());
                                 e.target.value = '';
                               }
                             }}
                           />
                           <button
                             onClick={(e) => {
                               const input = e.target.previousSibling;
                               if (input.value.trim()) {
                                 handleInputChange('location', input.value.trim());
                                 input.value = '';
                               }
                             }}
                             className="px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm"
                           >
                             Add
                           </button>
                         </div>
                       )}
                     </div>
                   </div>
                 </div>

                {/* Professional Bio */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                  <div className="flex items-start">
                    <FileText className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                    <textarea
                      value={tempData.professionalBio || ''}
                      onChange={(e) => handleInputChange('professionalBio', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Tell clients about your expertise and experience..."
                      rows={3}
                      maxLength={300}
                      className="flex-1 bg-transparent border-none outline-none text-gray-900 resize-none placeholder-gray-500"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-right">
                    {(tempData.professionalBio || '').length}/300
                  </p>
                </div>

                {/* Bar Council License */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                  <div className="flex items-center">
                    <BookOpen className="w-5 h-5 text-purple-600 mr-3" />
                    <div className="flex-1">
                      <div className="text-xs text-gray-800 mb-1">Bar Council ID / Registration Number</div>
                      <input
                        type="text"
                        value={tempData.barCouncilLicense || ''}
                        onChange={(e) => handleInputChange('barCouncilLicense', e.target.value.toUpperCase())}
                        disabled={!isEditing}
                        placeholder="e.g., MAH/1548/2003"
                        className="w-full bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Chamber Address */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                  <div className="flex items-start">
                    <Building className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                    <textarea
                      value={tempData.chamberAddress || ''}
                      onChange={(e) => handleInputChange('chamberAddress', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Chamber Address"
                      rows={3}
                      maxLength={500}
                      className="flex-1 bg-transparent border-none outline-none text-gray-900 resize-none placeholder-gray-500"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-right">
                    {(tempData.chamberAddress || '').length}/500
                  </p>
                </div>

                {/* Social Media Links */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Mail className="w-5 h-5 text-purple-600 mr-3" />
                      <input
                        type="email"
                        value={tempData.gmail || ''}
                        onChange={(e) => handleInputChange('gmail', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Gmail"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                      />
                    </div>
                    <div className="flex items-center">
                      <Linkedin className="w-5 h-5 text-purple-600 mr-3" />
                      <input
                        type="url"
                        value={tempData.linkedin || ''}
                        onChange={(e) => handleInputChange('linkedin', e.target.value)}
                        disabled={!isEditing}
                        placeholder="LinkedIn Profile URL"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                      />
                    </div>
                    <div className="flex items-center">
                      <Facebook className="w-5 h-5 text-purple-600 mr-3" />
                      <input
                        type="url"
                        value={tempData.facebook || ''}
                        onChange={(e) => handleInputChange('facebook', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Facebook Profile URL"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                      />
                    </div>
                    <div className="flex items-center">
                      <Instagram className="w-5 h-5 text-purple-600 mr-3" />
                      <input
                        type="url"
                        value={tempData.instagram || ''}
                        onChange={(e) => handleInputChange('instagram', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Instagram Profile URL"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                      />
                    </div>
                    <div className="flex items-center">
                      <Twitter className="w-5 h-5 text-purple-600 mr-3" />
                      <input
                        type="url"
                        value={tempData.twitter || ''}
                        onChange={(e) => handleInputChange('twitter', e.target.value)}
                        disabled={!isEditing}
                        placeholder="Twitter Profile URL"
                        className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
                     )}

           {/* Professional Details Tab */}
           {activeTab === 'professional' && (
             <div className="p-4">
               <div className="space-y-4">
                 {/* Specialization */}
                 <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                   <div className="flex items-start">
                     <Briefcase className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                     <div className="flex-1">
                       <div className="text-xs text-gray-800 mb-2">Legal Specializations</div>
                       <div className="flex flex-wrap gap-2 mb-3">
                         {legalSpecializations.map((spec) => (
                           <button
                             key={spec}
                             onClick={() => toggleSpecialization(spec)}
                             disabled={!isEditing}
                             className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                               selectedSpecializations.includes(spec)
                                 ? 'bg-purple-500 text-white'
                                 : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                             } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                           >
                             {spec}
                           </button>
                         ))}
                       </div>
                       {isEditing && (
                         <div className="flex items-center gap-2">
                           <input
                             type="text"
                             placeholder="Add custom specialization..."
                             className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                             onKeyPress={(e) => {
                               if (e.key === 'Enter' && e.target.value.trim()) {
                                 toggleSpecialization(e.target.value.trim());
                                 e.target.value = '';
                               }
                             }}
                           />
                           <button
                             onClick={(e) => {
                               const input = e.target.previousSibling;
                               if (input.value.trim()) {
                                 toggleSpecialization(input.value.trim());
                                 input.value = '';
                               }
                             }}
                             className="px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm"
                           >
                             Add
                           </button>
                         </div>
                       )}
                     </div>
                   </div>
                 </div>

                 {/* Years of Practice */}
                 <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                   <div className="flex items-center">
                     <Clock className="w-5 h-5 text-purple-600 mr-3" />
                     <div className="flex-1">
                       <div className="text-xs text-gray-800 mb-1">Years of Practice</div>
                       <input
                         type="text"
                         value={tempData.yearsOfPractice || ''}
                         onChange={(e) => handleInputChange('yearsOfPractice', e.target.value)}
                         disabled={!isEditing}
                         placeholder="e.g., 5 years"
                         className="w-full bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                       />
                     </div>
                   </div>
                 </div>

                 {/* Education */}
                 <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                   <div className="flex items-center justify-between mb-3">
                     <div className="flex items-center">
                       <GraduationCapIcon className="w-5 h-5 text-purple-600 mr-3" />
                       <div className="text-xs text-gray-800">Education</div>
                     </div>
                     {isEditing && (
                       <button
                         onClick={() => {
                           const newEducation = [...tempData.education, { qualification: '', degreeCollege: '' }];
                           setTempData(prev => ({ ...prev, education: newEducation }));
                         }}
                         className="text-purple-600 hover:text-purple-700 text-sm flex items-center"
                       >
                         <Plus className="w-4 h-4 mr-1" />
                         Add More
                       </button>
                     )}
                   </div>
                   
                   {tempData.education.map((edu, index) => (
                     <div key={index} className="mb-3 last:mb-0">
                       <div className="flex items-center justify-between mb-2">
                         <span className="text-xs text-gray-600">Education {index + 1}</span>
                         {isEditing && tempData.education.length > 1 && (
                           <button
                             onClick={() => {
                               const newEducation = tempData.education.filter((_, i) => i !== index);
                               setTempData(prev => ({ ...prev, education: newEducation }));
                             }}
                             className="text-red-500 hover:text-red-700"
                           >
                             <X className="w-4 h-4" />
                           </button>
                         )}
                       </div>
                       
                       <div className="space-y-2">
                         <input
                           type="text"
                           value={edu.qualification || ''}
                           onChange={(e) => {
                             const newEducation = [...tempData.education];
                             newEducation[index].qualification = e.target.value;
                             setTempData(prev => ({ ...prev, education: newEducation }));
                           }}
                           disabled={!isEditing}
                           placeholder="e.g., LL.B. (Bachelor of Laws)"
                           className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                         />
                         <input
                           type="text"
                           value={edu.degreeCollege || ''}
                           onChange={(e) => {
                             const newEducation = [...tempData.education];
                             newEducation[index].degreeCollege = e.target.value;
                             setTempData(prev => ({ ...prev, education: newEducation }));
                           }}
                           disabled={!isEditing}
                           placeholder="e.g., National Law School of India University"
                           className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                         />
                       </div>
                     </div>
                   ))}
                 </div>

                 {/* Year of Passing */}
                 <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                   <div className="flex items-center">
                     <Calendar className="w-5 h-5 text-purple-600 mr-3" />
                     <div className="flex-1">
                       <div className="text-xs text-gray-800 mb-1">Year of Passing</div>
                       <input
                         type="text"
                         value={tempData.yearOfPassing || ''}
                         onChange={(e) => handleInputChange('yearOfPassing', e.target.value)}
                         disabled={!isEditing}
                         placeholder="e.g., 2018"
                         className="w-full bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                       />
                     </div>
                   </div>
                 </div>

                 {/* Languages */}
                 <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                   <div className="flex items-start">
                     <Globe className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                     <div className="flex-1">
                       <div className="text-xs text-gray-800 mb-2">Languages Spoken</div>
                       <div className="flex flex-wrap gap-2 mb-3">
                         {availableLanguages.map((lang) => (
                           <button
                             key={lang}
                             onClick={() => toggleLanguage(lang)}
                             disabled={!isEditing}
                             className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                               selectedLanguages.includes(lang)
                                 ? 'bg-purple-500 text-white'
                                 : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                             } ${!isEditing ? 'opacity-50 cursor-not-allowed' : ''}`}
                           >
                             {lang}
                           </button>
                         ))}
                       </div>
                       {isEditing && (
                         <div className="flex items-center gap-2">
                           <input
                             type="text"
                             placeholder="Add custom language..."
                             className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                             onKeyPress={(e) => {
                               if (e.key === 'Enter' && e.target.value.trim()) {
                                 toggleLanguage(e.target.value.trim());
                                 e.target.value = '';
                               }
                             }}
                           />
                           <button
                             onClick={(e) => {
                               const input = e.target.previousSibling;
                               if (input.value.trim()) {
                                 toggleLanguage(input.value.trim());
                                 input.value = '';
                               }
                             }}
                             className="px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm"
                           >
                             Add
                           </button>
                         </div>
                       )}
                     </div>
                   </div>
                 </div>

                 {/* Achievements */}
                 <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                   <div className="flex items-start">
                     <AwardIcon className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                     <div className="flex-1">
                       <div className="text-xs text-gray-800 mb-2">Awards & Achievements</div>
                                               <div className="space-y-2 mb-3">
                          {tempData.achievements?.map((achievement, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={typeof achievement === 'object' ? achievement.text || achievement.achievement || '' : achievement}
                                onChange={(e) => {
                                  const newAchievements = [...tempData.achievements];
                                  if (typeof achievement === 'object') {
                                    newAchievements[index] = { ...achievement, text: e.target.value };
                                  } else {
                                    newAchievements[index] = e.target.value;
                                  }
                                  handleInputChange('achievements', newAchievements);
                                }}
                                disabled={!isEditing}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                placeholder="Enter achievement..."
                              />
                             {isEditing && (
                               <button
                                 onClick={() => {
                                   const newAchievements = tempData.achievements.filter((_, i) => i !== index);
                                   handleInputChange('achievements', newAchievements);
                                 }}
                                 className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                               >
                                 <Trash2 className="w-4 h-4" />
                               </button>
                             )}
                           </div>
                         ))}
                       </div>
                       {isEditing && (
                         <button
                           onClick={() => {
                             const newAchievements = [...(tempData.achievements || []), ''];
                             handleInputChange('achievements', newAchievements);
                           }}
                           className="flex items-center gap-2 text-purple-500 hover:text-purple-600 text-sm"
                         >
                           <Plus className="w-3 h-3" />
                           Add Achievement
                         </button>
                       )}
                     </div>
                   </div>
                 </div>
               </div>
             </div>
                       )}

            {/* Consultation Rates Tab */}
            {activeTab === 'consultation' && (
              <div className="p-4">
                <div className="space-y-4">
                  {/* Text Consultation Fee */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center">
                      <MessageSquare className="w-5 h-5 text-purple-600 mr-3" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-800 mb-1">Text Consultation Fee</div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">₹</span>
                          <input
                            type="number"
                            value={tempData.textConsultationFee || ''}
                            onChange={(e) => handleInputChange('textConsultationFee', e.target.value)}
                            disabled={!isEditing}
                            placeholder="0"
                            className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                          />
                          <span className="text-sm text-gray-500">per session</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Phone Call Fee */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center">
                      <Phone className="w-5 h-5 text-purple-600 mr-3" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-800 mb-1">Phone Call Rate</div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">₹</span>
                          <input
                            type="number"
                            value={tempData.callConsultationFee || ''}
                            onChange={(e) => handleInputChange('callConsultationFee', e.target.value)}
                            disabled={!isEditing}
                            placeholder="120"
                            className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                          />
                          <span className="text-sm text-gray-500">per session</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Video Call Fee */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center">
                      <Video className="w-5 h-5 text-purple-600 mr-3" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-800 mb-1">Video Call Rate</div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">₹</span>
                          <input
                            type="number"
                            value={tempData.videoCallConsultationFee || ''}
                            onChange={(e) => handleInputChange('videoCallConsultationFee', e.target.value)}
                            disabled={!isEditing}
                            placeholder="150"
                            className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                          />
                          <span className="text-sm text-gray-500">per session</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* In-Person Fee */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-center">
                      <User className="w-5 h-5 text-purple-600 mr-3" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-800 mb-1">In-Person Rate</div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">₹</span>
                          <input
                            type="number"
                            value={tempData.inPersonConsultationFee || ''}
                            onChange={(e) => handleInputChange('inPersonConsultationFee', e.target.value)}
                            disabled={!isEditing}
                            placeholder="300"
                            className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                          />
                          <span className="text-sm text-gray-500">per session</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Emergency Consultation */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-start">
                      <Zap className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs text-gray-800">Emergency Consultation Available</div>
                          <button
                            onClick={() => handleInputChange('emergencyConsultationAvailable', !tempData.emergencyConsultationAvailable)}
                            disabled={!isEditing}
                            className={`w-10 h-6 rounded-full transition-colors ${
                              tempData.emergencyConsultationAvailable
                                ? 'bg-purple-500'
                                : 'bg-gray-300'
                            } ${!isEditing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                              tempData.emergencyConsultationAvailable ? 'translate-x-5' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                        {tempData.emergencyConsultationAvailable && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">₹</span>
                            <input
                              type="number"
                              value={tempData.emergencyConsultationFee || ''}
                              onChange={(e) => handleInputChange('emergencyConsultationFee', e.target.value)}
                              disabled={!isEditing}
                              placeholder="500"
                              className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-500"
                            />
                            <span className="text-sm text-gray-500">emergency fee</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* File Reading Charges */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-start">
                      <FileText className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-800 mb-2">File Reading Charges</div>
                                                 <div className="space-y-2 mb-3">
                           {tempData.fileReadingCharges?.map((charge, index) => (
                             <div key={index} className="flex items-center gap-2">
                               <input
                                 type="text"
                                 value={typeof charge === 'object' ? (charge.service || charge.text || '') : charge}
                                 onChange={(e) => {
                                   const newCharges = [...tempData.fileReadingCharges];
                                   if (typeof charge === 'object') {
                                     newCharges[index] = { ...charge, service: e.target.value };
                                   } else {
                                     newCharges[index] = { service: e.target.value, amount: '' };
                                   }
                                   handleInputChange('fileReadingCharges', newCharges);
                                 }}
                                 disabled={!isEditing}
                                 placeholder="Service name (e.g., Document Review)"
                                 className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                               />
                               <input
                                 type="number"
                                 value={typeof charge === 'object' ? (charge.amount || charge.price || '') : ''}
                                 onChange={(e) => {
                                   const newCharges = [...tempData.fileReadingCharges];
                                   if (typeof charge === 'object') {
                                     newCharges[index] = { ...charge, amount: e.target.value };
                                   } else {
                                     newCharges[index] = { service: charge, amount: e.target.value };
                                   }
                                   handleInputChange('fileReadingCharges', newCharges);
                                 }}
                                 disabled={!isEditing}
                                 placeholder="Amount"
                                 className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                               />
                              {isEditing && (
                                <button
                                  onClick={() => {
                                    const newCharges = tempData.fileReadingCharges.filter((_, i) => i !== index);
                                    handleInputChange('fileReadingCharges', newCharges);
                                  }}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {isEditing && (
                          <button
                            onClick={() => {
                              const newCharges = [...(tempData.fileReadingCharges || []), { service: '', amount: '' }];
                              handleInputChange('fileReadingCharges', newCharges);
                            }}
                            className="flex items-center gap-2 text-purple-500 hover:text-purple-600 text-sm"
                          >
                            <Plus className="w-3 h-3" />
                            Add File Reading Charge
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

                                             {/* Accounts & Documents Tab */}
            {activeTab === 'accounts' && (
              <div className="p-4">
                <div className="space-y-4">
                  {/* ID Proof */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-start">
                      <Shield className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-800 mb-2">ID Proof (Aadhar/PAN/Passport) *</div>
                        
                        {/* ID Type Selection */}
                        <div className="mb-3">
                          <select
                            value={tempData.idProofType || ''}
                            onChange={(e) => handleInputChange('idProofType', e.target.value)}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                          >
                            <option value="">Select ID Type</option>
                            <option value="Aadhar">Aadhar Card</option>
                            <option value="PAN">PAN Card</option>
                            <option value="Passport">Passport</option>
                          </select>
                        </div>

                        {/* File Upload */}
                        {tempData.idProofType && (
                          <div className="space-y-2">
                            {idProofFile ? (
                              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-green-600" />
                                  <span className="text-sm text-green-800">ID Proof uploaded</span>
                                </div>
                                {isEditing && (
                                  <button
                                    onClick={() => removeFile('idProof')}
                                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <input
                                  type="file"
                                  accept="image/*,.pdf"
                                  onChange={(e) => handleFileUpload('idProof', e)}
                                  disabled={!isEditing}
                                  className="hidden"
                                  id="idProofUpload"
                                />
                                <label
                                  htmlFor="idProofUpload"
                                  className={`flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer transition-colors ${
                                    isEditing 
                                      ? 'hover:bg-gray-50 text-gray-700' 
                                      : 'opacity-50 cursor-not-allowed text-gray-500'
                                  }`}
                                >
                                  <Upload className="w-4 h-4" />
                                  <span className="text-sm">Upload ID Proof</span>
                                </label>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bar Council Certificate */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-start">
                      <BookOpen className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-800 mb-2">Bar Council Certificate / Practicing License *</div>
                        
                        {barCouncilFile ? (
                          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-green-800">Certificate uploaded</span>
                            </div>
                            {isEditing && (
                              <button
                                onClick={() => removeFile('barCouncil')}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileUpload('barCouncil', e)}
                              disabled={!isEditing}
                              className="hidden"
                              id="barCouncilUpload"
                            />
                            <label
                              htmlFor="barCouncilUpload"
                              className={`flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer transition-colors ${
                                isEditing 
                                  ? 'hover:bg-gray-50 text-gray-700' 
                                  : 'opacity-50 cursor-not-allowed text-gray-500'
                              }`}
                            >
                              <Upload className="w-4 h-4" />
                              <span className="text-sm">Upload Certificate</span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Selfie with ID */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-start">
                      <Camera className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-800 mb-2">Selfie holding ID (for verification) *</div>
                        
                        {selfieFile ? (
                          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <img src={selfieFile} alt="Selfie" className="w-8 h-8 rounded object-cover" />
                              <span className="text-sm text-green-800">Selfie uploaded</span>
                            </div>
                            {isEditing && (
                              <button
                                onClick={() => removeFile('selfie')}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileUpload('selfie', e)}
                              disabled={!isEditing}
                              className="hidden"
                              id="selfieUpload"
                            />
                            <label
                              htmlFor="selfieUpload"
                              className={`flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer transition-colors ${
                                isEditing 
                                  ? 'hover:bg-gray-50 text-gray-700' 
                                  : 'opacity-50 cursor-not-allowed text-gray-500'
                              }`}
                            >
                              <Camera className="w-4 h-4" />
                              <span className="text-sm">Take Photo / Upload Photo</span>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bank Details */}
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
                    <div className="flex items-start">
                      <CreditCard className="w-5 h-5 text-purple-600 mr-3 mt-1" />
                      <div className="flex-1">
                        <div className="text-xs text-gray-800 mb-3">Bank Details (where you will receive your payment)</div>
                        
                        <div className="space-y-3">
                          {/* Account Number */}
                          <div>
                            <div className="text-xs text-gray-600 mb-1">Account Number *</div>
                            <input
                              type="text"
                              value={tempData.accountNumber || ''}
                              onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                              disabled={!isEditing}
                              placeholder="Enter account number"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                            />
                          </div>

                          {/* Account Holder Name */}
                          <div>
                            <div className="text-xs text-gray-600 mb-1">Account Holder Name *</div>
                            <input
                              type="text"
                              value={tempData.accountHolderName || ''}
                              onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
                              disabled={!isEditing}
                              placeholder="Enter account holder name"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                            />
                          </div>

                          {/* Bank Name */}
                          <div>
                            <div className="text-xs text-gray-600 mb-1">Bank Name *</div>
                            <input
                              type="text"
                              value={tempData.bankName || ''}
                              onChange={(e) => handleInputChange('bankName', e.target.value)}
                              disabled={!isEditing}
                              placeholder="Enter bank name"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                            />
                          </div>

                          {/* IFSC Code */}
                          <div>
                            <div className="text-xs text-gray-600 mb-1">IFSC Code *</div>
                            <input
                              type="text"
                              value={tempData.ifscCode || ''}
                              onChange={(e) => handleInputChange('ifscCode', e.target.value.toUpperCase())}
                              disabled={!isEditing}
                              placeholder="Enter IFSC code"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

                     
        </motion.div>
      </div>
    </div>
  );
};

export default LawyerProfileSettings;
